// Shared Gmail / Drive panels. All data is fetched live as the signed-in user
// (see google-api.js) — read-only, no track@ involvement. Each panel manages
// its own connect / loading / error states; connecting once unlocks every
// panel on the page (they share the token and listen for 'google-connected').
// Folders drill down in place (breadcrumb trail) and files/emails open in an
// in-app overlay viewer — nothing leaves Track unless you ask it to.
import { escapeHtml, safeUrl } from '../ui.js';
import {
  isConfigured, hasToken, ensureToken, resumeToken, listThreads, gmailThreadUrl,
  getThreadFull, listFolder, isFolderMime, downloadDriveFile,
} from '../google-api.js';
import { openOverlay } from './overlay.js';

const fmtBytes = (b) => b >= 1e6 ? (b / 1e6).toFixed(1) + ' MB' : Math.max(1, Math.round(b / 1e3)) + ' KB';
const fmtDay = (d) => (d && !isNaN(d)) ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '';
const fmtFull = (d) => (d && !isNaN(d))
  ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) + ' ' +
    d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
  : '';

function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts.length === 1 ? parts[0].slice(0, 2) : parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Wire a panel body: not-configured note, connect button, then run() renders.
 */
function wire(body, run) {
  if (!isConfigured()) {
    body.innerHTML = `<p class="g-note">Google connection not configured yet — needs the OAuth client ID (see dashboard/GOOGLE-SETUP.md).</p>`;
    return;
  }
  if (hasToken()) { run(); return; }
  // Renew from the stored grant before offering Connect. These panels checked
  // only for a token under an hour old, so after any update or a morning's gap
  // they asked for Google again although the server still held the grant —
  // Mail, Files and Calendar already renewed silently (Karan, 21/10/2026).
  body.innerHTML = '<p class="g-note">Connecting to Google…</p>';
  resumeToken().catch(() => false).then(() => {
    if (hasToken()) run(); else connectPrompt(body, run);
  });
}

function connectPrompt(body, run) {
  body.innerHTML = `<div class="g-connect">
    <p class="g-note">Connect your Google account to see this — read-only, opens as you.</p>
    <button class="btn ghost g-connect-btn">Connect Gmail &amp; Drive</button>
  </div>`;
  const onConnected = () => run();
  window.addEventListener('google-connected', onConnected, { once: true });
  body.querySelector('.g-connect-btn').addEventListener('click', async (ev) => {
    ev.target.disabled = true;
    try {
      await ensureToken(); // user gesture → GIS popup
      window.dispatchEvent(new Event('google-connected')); // unlock sibling panels
    } catch (e) {
      ev.target.disabled = false;
      const note = body.querySelector('.g-note');
      if (note) { note.classList.add('error'); note.textContent = e.message || String(e); }
    }
  });
}

// ── Email thread viewer (in-app, read-only) ─────────────────────────────────

// Email HTML is untrusted (authored by external strangers). Two layers:
// 1. DOMPurify allowlist sanitisation (scripts, event handlers, forms, meta,
//    base and dangerous URLs stripped; links forced to target=_blank noopener).
// 2. The sandboxed iframe (no allow-scripts) as a backstop.
// If DOMPurify somehow isn't loaded, we REFUSE to render HTML and fall back
// to the plain-text body — never raw HTML without the sanitiser.
let purifyHooked = false;
function sanitizeEmailHtml(html) {
  if (typeof DOMPurify === 'undefined' || !DOMPurify.isSupported) return null;
  if (!purifyHooked) {
    purifyHooked = true;
    DOMPurify.addHook('afterSanitizeAttributes', (node) => {
      if (node.tagName === 'A') {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
      }
    });
  }
  return DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ['form', 'input', 'button', 'select', 'textarea', 'meta', 'base', 'link', 'iframe', 'object', 'embed'],
    FORBID_ATTR: ['formaction'],
  });
}

function messageSrcdoc(m) {
  const style = `<style>body{font:14px/1.55 -apple-system,'Helvetica Neue',Arial,sans-serif;color:#1a1a2e;margin:14px}
    img{max-width:100%;height:auto}table{max-width:100%}blockquote{border-left:3px solid #DEDEDA;margin:8px 0;padding-left:12px;color:#55564F}</style>`;
  const clean = m.html ? sanitizeEmailHtml(m.html) : null;
  if (clean != null) return style + clean;
  return style + `<div style="white-space:pre-wrap">${escapeHtml(m.text || (m.html ? '(HTML body hidden — sanitiser unavailable)' : '(no body)'))}</div>`;
}

function openThread(t) {
  const { content } = openOverlay({
    title: t.subject || '(no subject)',
    subtitle: `${t.messageCount} message${t.messageCount === 1 ? '' : 's'}`,
    wide: true,
    actionsHtml: `<a class="ov-link" href="${escapeHtml(gmailThreadUrl(t.id))}" target="_blank" rel="noopener">Open in Gmail →</a>`,
  });
  content.innerHTML = `<p class="g-note" style="padding:18px 22px">Loading thread…</p>`;

  getThreadFull(t.id).then((messages) => {
    content.innerHTML = messages.map((m, i) => `
      <div class="mail-msg">
        <div class="mail-msg-head">
          <span class="g-avatar">${escapeHtml(initials(m.from.name))}</span>
          <span class="mail-msg-who">
            <span class="mail-msg-from">${escapeHtml(m.from.name)}
              <span class="mail-msg-email">${escapeHtml(m.from.email ? `<${m.from.email}>` : '')}</span></span>
            <span class="mail-msg-to">to ${escapeHtml(m.to || '—')}</span>
          </span>
          <span class="mail-msg-date">${escapeHtml(fmtFull(m.date))}</span>
        </div>
        <iframe class="mail-iframe" sandbox="allow-same-origin allow-popups" data-i="${i}"></iframe>
        ${m.attachments.length ? `<div class="mail-atts">${m.attachments.map((a) => `
          <span class="mail-att" title="${escapeHtml(a.mimeType)}">📎 ${escapeHtml(a.filename)}
            <span class="mail-att-size">${a.size ? Math.round(a.size / 1024) + ' KB' : ''}</span></span>`).join('')}
          <span class="g-note" style="font-size:11.5px">attachments open via Gmail</span></div>` : ''}
      </div>`).join('');

    content.querySelectorAll('.mail-iframe').forEach((frame) => {
      const m = messages[+frame.dataset.i];
      frame.addEventListener('load', () => {
        try { frame.style.height = Math.min(Math.max(frame.contentDocument.body.scrollHeight + 30, 60), 1600) + 'px'; }
        catch (e) { frame.style.height = '420px'; }
      });
      frame.srcdoc = messageSrcdoc(m);
    });
    content.scrollTop = content.scrollHeight; // land on the latest message
  }).catch((e) => {
    content.innerHTML = `<p class="g-note error" style="padding:18px 22px">${escapeHtml(e.message || e)}</p>`;
  });
}

function threadRow(t, i) {
  const dots = '<span class="g-unread"></span>'.repeat(Math.min(t.unread, 3));
  return `<div class="g-mail-row" data-i="${i}" role="button" tabindex="0">
    <span class="g-avatar">${escapeHtml(initials(t.from.name))}</span>
    <span class="g-mail-main">
      <span class="g-mail-top">
        <span class="g-mail-from">${escapeHtml(t.from.name)}</span>${dots}
        <span class="g-mail-meta">${t.messageCount > 1 ? t.messageCount + ' · ' : ''}${escapeHtml(fmtDay(t.date))}</span>
      </span>
      <span class="g-mail-subject">${escapeHtml(t.subject)}</span>
      <span class="g-mail-snippet">${escapeHtml(t.snippet)}</span>
    </span>
  </div>`;
}

/** Gmail panel. q='' → the user's recent threads; pass a query for project views. */
export function emailPanel(container, { q = '', maxResults = 25, emptyText = 'No emails found.', threadId = null } = {}) {
  const body = document.createElement('div');
  body.className = 'g-body';
  container.appendChild(body);
  wire(body, async () => {
    body.innerHTML = `<p class="g-note">Loading…</p>`;
    try {
      // Scoped to a single thread (an enquiry knows its own Gmail thread) — show
      // ONLY that conversation, not everything from the sender (Karan 13/07).
      if (threadId) {
        const messages = await getThreadFull(threadId);
        if (!messages.length) { body.innerHTML = `<p class="g-note">${escapeHtml(emptyText)}</p>`; return; }
        const first = messages.find((m) => m.from) || messages[0];
        const last = messages[messages.length - 1];
        const t = {
          id: threadId,
          from: first.from || { name: '', email: '' },
          subject: first.subject || '(no subject)',
          snippet: String(last.snippet || last.body || '').slice(0, 140),
          date: last.date, unread: 0, messageCount: messages.length,
        };
        body.innerHTML = threadRow(t, 0);
        const row = body.querySelector('.g-mail-row');
        if (row) {
          const open = () => openThread(t);
          row.addEventListener('click', open);
          row.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') open(); });
        }
        return;
      }
      const threads = await listThreads({ q, maxResults });
      if (!threads.length) { body.innerHTML = `<p class="g-note">${escapeHtml(emptyText)}</p>`; return; }
      body.innerHTML = threads.map(threadRow).join('');
      body.querySelectorAll('.g-mail-row').forEach((row) => {
        const open = () => openThread(threads[+row.dataset.i]);
        row.addEventListener('click', open);
        row.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') open(); });
      });
    } catch (e) { body.innerHTML = `<p class="g-note error">${escapeHtml(e.message || e)}</p>`; }
  });
}

// ── Drive: navigable folder browser + in-app file viewer ────────────────────

function fileTag(f) {
  if (isFolderMime(f.mimeType)) return 'DIR';
  const gapps = { document: 'DOC', spreadsheet: 'SHT', presentation: 'SLD' };
  const g = String(f.mimeType).match(/vnd\.google-apps\.(\w+)/);
  if (g) return gapps[g[1]] || g[1].slice(0, 3).toUpperCase();
  const ext = String(f.name).match(/\.(\w{1,4})$/);
  return ext ? ext[1].toUpperCase() : 'FILE';
}

// Embeddable preview URL (Drive's own viewer): PDFs, images, video, Office
// files via /file/d/…/preview; native Google files via their /preview routes.
function previewUrl(f) {
  const g = String(f.mimeType).match(/vnd\.google-apps\.(\w+)/);
  if (g) {
    const map = { document: 'document', spreadsheet: 'spreadsheets', presentation: 'presentation' };
    return map[g[1]] ? `https://docs.google.com/${map[g[1]]}/d/${encodeURIComponent(f.id)}/preview` : null;
  }
  return `https://drive.google.com/file/d/${encodeURIComponent(f.id)}/preview`;
}

// CAD files (Karan 07/07 — "there must be an open source option"): DWGs are
// converted IN THE BROWSER (LibreDWG → WASM, vendored) to SVG and shown with
// pan/zoom. The drawing never leaves Google + this page: bytes come straight
// from Drive with the user's token, conversion is local, output is
// DOMPurify-sanitised (house rule 0). Anything the converter can't handle
// falls back to the "Open in Drive → AutoCAD Web App" card.
const isCad = (f) => /\.dwg$/i.test(f.name || '') || String(f.mimeType) === 'image/vnd.dwg';
const isTedds = (f) => /\.ted$/i.test(f.name || '');
const isMasonry = (f) => /\.msnx$/i.test(f.name || '');
const is3d = (f) => /\.(ifc|step|stp)$/i.test(f.name || '');
const isPointCloud = (f) => /\.xyz$/i.test(f.name || '');
const CAD3D_MAX = 60 * 1024 * 1024;
const XYZ_MAX = 120 * 1024 * 1024; // ASCII point clouds are big; parser decimates beyond ~2.5M pts
const CAD_MAX_BYTES = 80 * 1024 * 1024; // WASM heap sanity cap

function cadFallback(content, f, note) {
  content.innerHTML = `
    <div style="padding:26px 28px">
      <p style="margin:0;font-size:14.5px;color:var(--ink)">${escapeHtml(note || 'This drawing could not be converted in the browser.')}</p>
      <p class="g-note" style="margin:10px 0 18px">Open it in Drive and use
        <b>Open with → AutoCAD Web App</b>, or open the file in AutoCAD on your machine.</p>
      <a class="btn primary" style="text-decoration:none" href="${escapeHtml(safeUrl(f.webViewLink))}"
        target="_blank" rel="noopener">Open in Drive</a>
    </div>`;
}

// Robust viewBox: the converter's own extents blow up when drawings contain
// stray far-away entities. Measure every top-level element and take the
// densest cluster (median-anchored) instead of the raw union.
function fitViewBox(svgEl) {
  const boxes = [];
  for (const el of svgEl.children) {
    if (el.tagName === 'defs') continue;
    try {
      const b = el.getBBox();
      if (isFinite(b.x) && isFinite(b.y) && b.width > 0 && b.height > 0
          && b.width < 1e12 && b.height < 1e12) boxes.push(b);
    } catch (e) { /* non-rendered element */ }
  }
  if (!boxes.length) return;
  const med = (arr) => arr.sort((a, b) => a - b)[Math.floor(arr.length / 2)];
  const cx = med(boxes.map((b) => b.x + b.width / 2));
  const cy = med(boxes.map((b) => b.y + b.height / 2));
  const scale = Math.max(med(boxes.map((b) => Math.max(b.width, b.height))), 1);
  // Keep boxes whose centre sits within a generous radius of the median centre.
  const keep = boxes.filter((b) => Math.hypot(b.x + b.width / 2 - cx, b.y + b.height / 2 - cy) < scale * 500);
  const use = keep.length ? keep : boxes;
  const x0 = Math.min(...use.map((b) => b.x)), y0 = Math.min(...use.map((b) => b.y));
  const x1 = Math.max(...use.map((b) => b.x + b.width)), y1 = Math.max(...use.map((b) => b.y + b.height));
  const pad = Math.max(x1 - x0, y1 - y0) * 0.03;
  svgEl.setAttribute('viewBox', `${x0 - pad} ${y0 - pad} ${(x1 - x0) + pad * 2} ${(y1 - y0) + pad * 2}`);
}

// Canvas rendering engine (Karan 07/07 — "still very slow"): the drawing is
// rasterised ONCE per view window instead of living as thousands of SVG DOM
// nodes. Gestures move the bitmap on the GPU (instant at any drawing size);
// when the gesture settles, the vectors re-rasterise crisply for the new
// window in the background. Converted SVG is cached (Cache API) per file
// version, so reopening a drawing skips download + conversion entirely.

// Rewrite the opening <svg> tag with our window + pixel size.
function svgWindow(svgText, vb, W, H) {
  const m = svgText.match(/<svg[^>]*>/);
  if (!m) return null;
  let open = m[0]
    .replace(/\sviewBox="[^"]*"/, '')
    .replace(/\swidth="[^"]*"/, '')
    .replace(/\sheight="[^"]*"/, '')
    .replace(/\spreserveAspectRatio="[^"]*"/, '');
  open = open.slice(0, -1) +
    ` viewBox="${vb.x} ${vb.y} ${vb.w} ${vb.h}" width="${W}" height="${H}" preserveAspectRatio="xMidYMid meet">`;
  return svgText.replace(m[0], open);
}

// Expand a box to exactly the stage aspect ratio (keeps the commit maths exact).
function toAspect(vb, W, H) {
  let { x, y, w, h } = vb;
  const a = W / H;
  if (w / h > a) { const nh = w / a; y -= (nh - h) / 2; h = nh; }
  else { const nw = h * a; x -= (nw - w) / 2; w = nw; }
  return { x, y, w, h };
}

function cadCanvasViewer(stage, svgText, fitted) {
  const canvas = document.createElement('canvas');
  stage.appendChild(canvas);
  canvas.style.transformOrigin = '0 0';
  canvas.style.willChange = 'transform';
  canvas.style.cursor = 'grab';

  const W = () => stage.clientWidth, H = () => stage.clientHeight;
  let vb = toAspect(fitted, W(), H());
  let scale = 1, tx = 0, ty = 0, raf = null, commitTimer = null, drag = null, paintSeq = 0;

  async function paint() {
    const seq = ++paintSeq;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const text = svgWindow(svgText, vb, Math.round(W() * dpr), Math.round(H() * dpr));
    if (!text) return;
    const url = URL.createObjectURL(new Blob([text], { type: 'image/svg+xml' }));
    try {
      const img = new Image();
      await new Promise((res, rej) => { img.onload = res; img.onerror = () => rej(new Error('raster failed')); img.src = url; });
      if (seq !== paintSeq) return; // a newer window superseded this paint
      canvas.width = Math.round(W() * dpr); canvas.height = Math.round(H() * dpr);
      canvas.style.width = W() + 'px'; canvas.style.height = H() + 'px';
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      canvas.style.transform = '';
      scale = 1; tx = 0; ty = 0;
    } finally { URL.revokeObjectURL(url); }
  }

  const schedule = () => {
    if (!raf) raf = requestAnimationFrame(() => {
      raf = null;
      canvas.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
    });
  };
  const commit = () => {
    commitTimer = null;
    if (scale === 1 && !tx && !ty) return;
    vb = {
      x: vb.x - (tx * vb.w) / (W() * scale),
      y: vb.y - (ty * vb.h) / (H() * scale),
      w: vb.w / scale,
      h: vb.h / scale,
    };
    paint().catch(() => {});
  };
  const scheduleCommit = (ms) => { clearTimeout(commitTimer); commitTimer = setTimeout(commit, ms); };

  canvas.addEventListener('wheel', (ev) => {
    ev.preventDefault();
    const r = stage.getBoundingClientRect();
    const cx = ev.clientX - r.left, cy = ev.clientY - r.top;
    const k = ev.deltaY > 0 ? 1 / 1.13 : 1.13;
    if (scale * k < 0.02 || scale * k > 600) return;
    tx = cx - k * (cx - tx);
    ty = cy - k * (cy - ty);
    scale *= k;
    schedule();
    scheduleCommit(160);
  }, { passive: false });
  canvas.addEventListener('pointerdown', (ev) => {
    clearTimeout(commitTimer);
    drag = { x: ev.clientX - tx, y: ev.clientY - ty };
    canvas.setPointerCapture(ev.pointerId);
    canvas.style.cursor = 'grabbing';
  });
  canvas.addEventListener('pointermove', (ev) => {
    if (!drag) return;
    tx = ev.clientX - drag.x;
    ty = ev.clientY - drag.y;
    schedule();
  });
  canvas.addEventListener('pointerup', () => { drag = null; canvas.style.cursor = 'grab'; scheduleCommit(50); });

  paint().catch(() => {});
}

// Conversion runs in /vendor/libredwg/convert.html — an invisible same-origin
// iframe with ITS OWN CSP (embind needs 'unsafe-eval'; the main app's CSP
// stays strict). One iframe per session, reused across drawings.
let cadFrame = null, cadReady = null, cadSeq = 0;
function ensureCadFrame() {
  if (cadReady) return cadReady;
  cadFrame = document.createElement('iframe');
  cadFrame.style.display = 'none';
  cadFrame.src = '/vendor/libredwg/convert.html';
  document.body.appendChild(cadFrame);
  cadReady = new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('Converter failed to load')), 30000);
    const onMsg = (ev) => {
      if (ev.origin === location.origin && ev.data && ev.data.type === 'dwg-ready') {
        clearTimeout(t); window.removeEventListener('message', onMsg); resolve();
      }
    };
    window.addEventListener('message', onMsg);
  });
  return cadReady;
}
async function convertInSandbox(bytes) {
  await ensureCadFrame();
  const id = ++cadSeq;
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => { window.removeEventListener('message', onMsg); reject(new Error('Conversion timed out (2 min)')); }, 120000);
    const onMsg = (ev) => {
      if (ev.origin !== location.origin || !ev.data || ev.data.type !== 'dwg-result' || ev.data.id !== id) return;
      clearTimeout(t); window.removeEventListener('message', onMsg);
      ev.data.error ? reject(new Error(ev.data.error)) : resolve(ev.data.svg);
    };
    window.addEventListener('message', onMsg);
    cadFrame.contentWindow.postMessage({ type: 'dwg-convert', id, bytes }, location.origin, [bytes]);
  });
}

async function openCadViewer(f) {
  const { content } = openOverlay({
    title: f.name,
    subtitle: fmtDay(f.modifiedTime ? new Date(f.modifiedTime) : null),
    wide: true,
    actionsHtml: `<a class="ov-link" href="${escapeHtml(safeUrl(f.webViewLink))}" target="_blank" rel="noopener">Open in Drive →</a>`,
  });
  content.classList.add('viewer');
  const status = (t) => { content.innerHTML = `<div style="padding:40px;text-align:center"><p class="g-note">${escapeHtml(t)}</p><div class="skel" style="height:8px;max-width:320px;margin:14px auto"></div></div>`; };
  try {
    // Converted-drawing cache: keyed by file id + version, so re-opens are instant.
    const cacheKey = `https://asp-track.web.app/__cad/${encodeURIComponent(f.id)}?v=${encodeURIComponent(f.modifiedTime || '')}`;
    let cache = null, svgText = null;
    try { cache = await caches.open('track-cad-v2'); } catch (e) { /* private mode etc. */ }
    if (cache) {
      const hit = await cache.match(cacheKey);
      if (hit) svgText = await hit.text();
    }

    if (!svgText) {
      status('Fetching drawing from Drive…');
      const bytes = await downloadDriveFile(f.id);
      if (bytes.byteLength > CAD_MAX_BYTES) { cadFallback(content, f, 'This drawing is too large to convert in the browser.'); return; }
      status(`Converting ${(bytes.byteLength / 1048576).toFixed(1)} MB drawing… (first open loads the converter, ~9 MB)`);
      const rawSvg = await convertInSandbox(bytes);
      if (!rawSvg || rawSvg.length < 100) { cadFallback(content, f, 'The converter produced no drawable geometry.'); return; }
      if (!window.DOMPurify) { cadFallback(content, f, 'Sanitiser unavailable — not rendering unchecked SVG.'); return; }
      // Serialise the sanitised DOM as XML — DOMPurify's string output is
      // HTML-serialised, which drops xmlns and breaks SVG-as-image loading.
      const frag = window.DOMPurify.sanitize(rawSvg, { USE_PROFILES: { svg: true, svgFilters: true }, RETURN_DOM_FRAGMENT: true });
      const svgNode = frag.querySelector('svg');
      if (!svgNode) { cadFallback(content, f, 'The converter produced no drawable geometry.'); return; }
      svgText = new XMLSerializer().serializeToString(svgNode);
      if (cache) cache.put(cacheKey, new Response(svgText, { headers: { 'Content-Type': 'image/svg+xml' } })).catch(() => {});
    }

    // Measure once in a hidden mount to compute the fitted window (getBBox
    // needs layout), then the DOM copy is discarded — the canvas takes over.
    status('Preparing view…');
    const meas = document.createElement('div');
    meas.style.cssText = 'position:absolute;left:-99999px;top:0;width:1400px;height:900px;overflow:hidden;visibility:hidden';
    meas.innerHTML = svgText;
    document.body.appendChild(meas);
    const msvg = meas.querySelector('svg');
    if (!msvg) { meas.remove(); cadFallback(content, f, 'The converter produced no drawable geometry.'); return; }
    fitViewBox(msvg);
    const [fx, fy, fw, fh] = msvg.getAttribute('viewBox').split(/\s+/).map(Number);
    meas.remove();

    content.innerHTML = `<div class="cad-wrap"><div class="cad-stage">
      <div class="cad-hint">Scroll to zoom · drag to pan · open-source preview — use AutoCAD for measurements</div></div></div>`;
    cadCanvasViewer(content.querySelector('.cad-stage'), svgText, { x: fx, y: fy, w: fw, h: fh });
  } catch (e) {
    cadFallback(content, f, `Conversion failed: ${e.message || e}`);
  }
}

async function openTeddsViewer(f) {
  const { content } = openOverlay({
    title: f.name,
    subtitle: fmtDay(f.modifiedTime ? new Date(f.modifiedTime) : null),
    wide: true,
    actionsHtml: `<a class="ov-link" href="${escapeHtml(safeUrl(f.webViewLink))}" target="_blank" rel="noopener">Open in Drive →</a>`,
  });
  content.innerHTML = `<div style="padding:40px;text-align:center"><p class="g-note">Reading calculation…</p><div class="skel" style="height:8px;max-width:320px;margin:14px auto"></div></div>`;
  try {
    const [ted, bytes] = await Promise.all([
      import('../tedds.js'), downloadDriveFile(f.id),
    ]);
    const { parseTedds, teddsHtml, ensureTeddsLibs } = ted;
    const parsed = parseTedds(new TextDecoder('utf-8').decode(bytes));
    if (!parsed.calcs.length) throw new Error('No calculation output found in this file.');
    // Load the EMF/WMF renderers so vector diagrams draw inline; if they fail to
    // load, teddsHtml falls back to a labelled placeholder rather than breaking.
    try { await ensureTeddsLibs(); } catch (_) { /* fallbacks handle it */ }
    content.innerHTML = `<div class="ted-scroll">${teddsHtml(parsed, f.name)}</div>`;
  } catch (e) {
    content.innerHTML = `<div style="padding:26px 28px">
      <p style="margin:0;font-size:14.5px;color:var(--ink)">Couldn't render this Tedds file: ${escapeHtml(e.message || String(e))}</p>
      <p class="g-note" style="margin:10px 0 18px">Open it in Tedds, or view the issued PDF.</p>
      <a class="btn primary" style="text-decoration:none" href="${escapeHtml(safeUrl(f.webViewLink))}" target="_blank" rel="noopener">Open in Drive</a></div>`;
  }
}

// MSNX (MasterSeries Masonry) is plain XML — parse the stored brief inputs +
// preview image and show them; never derive or recalculate results (Sol 13/07).
async function openMasonryViewer(f) {
  const { content } = openOverlay({
    title: f.name,
    subtitle: fmtDay(f.modifiedTime ? new Date(f.modifiedTime) : null),
    wide: true,
    actionsHtml: `<a class="ov-link" href="${escapeHtml(safeUrl(f.webViewLink))}" target="_blank" rel="noopener">Open in Drive →</a>`,
  });
  content.innerHTML = `<div style="padding:40px;text-align:center"><p class="g-note">Reading masonry model…</p><div class="skel" style="height:8px;max-width:320px;margin:14px auto"></div></div>`;
  try {
    const [viewer, bytes] = await Promise.all([
      import('../msnx.js'), downloadDriveFile(f.id),
    ]);
    const parsed = viewer.parseMasonry(new TextDecoder('utf-8').decode(bytes));
    content.innerHTML = `<div class="msnx-scroll">${viewer.masonryHtml(parsed)}</div>`;
  } catch (e) {
    content.innerHTML = `<div style="padding:26px 28px">
      <p style="margin:0;font-size:14.5px;color:var(--ink)">Couldn't render this MasterSeries Masonry file: ${escapeHtml(e.message || String(e))}</p>
      <p class="g-note" style="margin:10px 0 18px">The original file has not been changed.</p>
      <a class="btn primary" style="text-decoration:none" href="${escapeHtml(safeUrl(f.webViewLink))}" target="_blank" rel="noopener">Open in Drive</a></div>`;
  }
}

// The classic PDF.js viewer is retired (Karan 10/08) — every PDF opens in the
// drawing reviewer, which saves markup as a sibling _MARKUP file (§11.5).
const isPdf = (f) => String(f.mimeType) === 'application/pdf' || /\.pdf$/i.test(f.name || '');

// IFC / STEP → in-browser 3D (three.js + web-ifc/occt WASM). Model bytes come
// from Drive; parsing + render are local; nothing leaves Track (Karan 07/07).
let webifcLoaded = false;
async function openThreeDViewer(f) {
  const { content, onClose } = openOverlay({
    title: f.name,
    subtitle: fmtDay(f.modifiedTime ? new Date(f.modifiedTime) : null),
    wide: true,
    actionsHtml: `<a class="ov-link" href="${escapeHtml(safeUrl(f.webViewLink))}" target="_blank" rel="noopener">Open in Drive →</a>`,
  });
  content.classList.add('viewer');
  const status = (t) => { content.innerHTML = `<div style="padding:40px;text-align:center"><p class="g-note">${escapeHtml(t)}</p><div class="skel" style="height:8px;max-width:320px;margin:14px auto"></div></div>`; };
  try {
    status('Fetching model from Drive…');
    const bytes = await downloadDriveFile(f.id);
    if (bytes.byteLength > CAD3D_MAX) { cadFallback(content, f, 'This model is too large to view in the browser.'); return; }
    status(`Building 3D model… (first open loads the engine, ~7 MB)`);
    const viewer = await import('../viewer3d.js');
    let geoms;
    if (/\.ifc$/i.test(f.name)) {
      if (!webifcLoaded) { await loadScript('/vendor/webifc/web-ifc-api-iife.js'); webifcLoaded = true; }
      geoms = await viewer.loadIfc(bytes, location.origin + '/vendor/webifc/');
    } else {
      geoms = await convertStepInSandbox(bytes); // occt needs unsafe-eval → isolated iframe
    }
    if (!geoms || !geoms.length) { cadFallback(content, f, 'No geometry found in this model.'); return; }
    content.innerHTML = `<div class="cad-wrap"><div class="cad-stage"><canvas class="d3-canvas"></canvas>
      <div class="cad-hint">Drag to orbit · scroll to zoom · right-drag to pan · open-source 3D preview</div></div></div>`;
    const info = viewer.meshesToScene(content.querySelector('.d3-canvas'), geoms);
    if (info && info.dispose) onClose(info.dispose); // free the WebGL context + stop the render loop on close
  } catch (e) {
    cadFallback(content, f, `Couldn't build the 3D model: ${e.message || e}`);
  }
}
// .xyz point clouds → in-browser 3D (three.js Points). Pure-JS parse, no WASM,
// so nothing leaves Track: bytes come from Drive with the user's token and
// render locally (Karan 12/07). Large clouds are decimated for preview.
async function openPointCloudViewer(f) {
  const { content, onClose } = openOverlay({
    title: f.name,
    subtitle: fmtDay(f.modifiedTime ? new Date(f.modifiedTime) : null),
    wide: true,
    actionsHtml: `<a class="ov-link" href="${escapeHtml(safeUrl(f.webViewLink))}" target="_blank" rel="noopener">Open in Drive →</a>`,
  });
  content.classList.add('viewer');
  const status = (t) => { content.innerHTML = `<div style="padding:40px;text-align:center"><p class="g-note">${escapeHtml(t)}</p><div class="skel" style="height:8px;max-width:320px;margin:14px auto"></div></div>`; };
  const fallback = (note) => {
    content.innerHTML = `<div style="padding:26px 28px">
      <p style="margin:0;font-size:14.5px;color:var(--ink)">${escapeHtml(note)}</p>
      <p class="g-note" style="margin:10px 0 18px">Open it in Drive, or in your point-cloud tool (e.g. CloudCompare / Recap).</p>
      <a class="btn primary" style="text-decoration:none" href="${escapeHtml(safeUrl(f.webViewLink))}" target="_blank" rel="noopener">Open in Drive</a></div>`;
  };
  try {
    status('Fetching point cloud from Drive…');
    const bytes = await downloadDriveFile(f.id);
    if (bytes.byteLength > XYZ_MAX) { fallback('This point cloud is too large to view in the browser.'); return; }
    status('Reading points…');
    const viewer = await import('../viewer3d.js');
    const cloud = viewer.parseXyz(new TextDecoder('utf-8').decode(bytes));
    if (!cloud.count) { fallback('No point data found in this .xyz file.'); return; }
    content.innerHTML = `<div class="cad-wrap"><div class="cad-stage"><canvas class="d3-canvas"></canvas>
      <div class="cad-hint">${cloud.count.toLocaleString()} points${cloud.decimated ? ' · thinned for preview' : ''} · drag to orbit · scroll to zoom · right-drag to pan</div></div></div>`;
    const info = viewer.pointCloudToScene(content.querySelector('.d3-canvas'), cloud.position, cloud.colors);
    if (info && info.dispose) onClose(info.dispose); // free the WebGL context + stop the render loop on close
  } catch (e) {
    fallback(`Couldn't render this point cloud: ${e.message || e}`);
  }
}

// STEP conversion runs in /vendor/occt/convert.html (its own relaxed CSP).
let stepFrame = null, stepReady = null, stepSeq = 0;
function ensureStepFrame() {
  if (stepReady) return stepReady;
  stepFrame = document.createElement('iframe');
  stepFrame.style.display = 'none';
  stepFrame.src = '/vendor/occt/convert.html';
  document.body.appendChild(stepFrame);
  stepReady = new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('STEP engine failed to load')), 30000);
    const onMsg = (ev) => {
      if (ev.origin === location.origin && ev.data && ev.data.type === 'step-ready') {
        clearTimeout(t); window.removeEventListener('message', onMsg); resolve();
      }
    };
    window.addEventListener('message', onMsg);
  });
  return stepReady;
}
async function convertStepInSandbox(bytes) {
  await ensureStepFrame();
  const id = ++stepSeq;
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => { window.removeEventListener('message', onMsg); reject(new Error('STEP conversion timed out')); }, 120000);
    const onMsg = (ev) => {
      if (ev.origin !== location.origin || !ev.data || ev.data.type !== 'step-result' || ev.data.id !== id) return;
      clearTimeout(t); window.removeEventListener('message', onMsg);
      ev.data.error ? reject(new Error(ev.data.error)) : resolve(ev.data.geoms);
    };
    window.addEventListener('message', onMsg);
    stepFrame.contentWindow.postMessage({ type: 'step-convert', id, bytes }, location.origin, [bytes]);
  });
}
function loadScript(src) {
  return new Promise((res, rej) => {
    const s = document.createElement('script');
    s.src = src; s.onload = res; s.onerror = () => rej(new Error('failed to load ' + src));
    document.head.appendChild(s);
  });
}

// PDFs opened from a project's Documents tab go to the drawing REVIEWER, not
// the plain PDF viewer (Karan 08/08 — no separate Drawings & models screen;
// review is just how a document opens). `opts.reviewer` carries {reference,
// title, status, user}; absent → classic behaviour everywhere else.
async function openInReviewer(f, r = {}) {
  try {
    // Open the viewer IMMEDIATELY (click feedback — Karan 08/08); it shows
    // "Fetching from Drive…" while the bytes arrive as a promise.
    const m = await import('./drawing-review.js');
    const bytesPromise = downloadDriveFile(f.id);
    m.openDrawingReview({
      data: bytesPromise,
      reference: r.reference || String(f.name || '').replace(/\.pdf$/i, ''),
      title: r.title || '',
      status: r.status || '',
      user: r.user,
      email: r.email || null,
      projectNo: r.projectNo || null,           // a comment records which job it is about
      file: { id: f.id, name: f.name || '', webViewLink: f.webViewLink || '' },   // Save markup writes a sibling _MARKUP file (§11.5)
    });
    await bytesPromise;                // surface download failure below
  } catch (e) {
    // ONE viewer (Karan 10/08) — no classic fallback. Fail honestly instead.
    const m = await import('./drawing-review.js').catch(() => null);
    m?.closeDrawingReview?.();
    const { content } = openOverlay({ title: f.name || 'PDF', reading: true });
    // A tab left open across a deploy asks for a viewer file that deploy
    // replaced (18/10); say so plainly and offer the reload, not the browser's words.
    if (/MIME type|dynamically imported module|Importing a module script failed/i.test(String(e && e.message))) {
      content.innerHTML = `<p class="g-note">Track has been updated since this tab was opened. Reload to open the drawing.</p>
        <p><button type="button" class="btn primary" data-reload>Reload Track</button></p>`;
      content.querySelector('[data-reload]').addEventListener('click', () => location.reload());
      return;
    }
    content.innerHTML = `<p class="g-note error">Couldn't open this sheet — ${escapeHtml(e.message || 'download failed')}.</p>
      ${f.webViewLink ? `<p class="g-note"><a href="${escapeHtml(safeUrl(f.webViewLink))}" target="_blank" rel="noopener">Open in Drive ↗</a></p>` : ''}`;
  }
}

// ── Flicking between files (Karan 08/10): ← / → in any viewer opens the
// previous / next file of the list it was opened from — the folder, the
// to-fix list, a project's documents — without closing it first. Callers pass
// `siblings` (the files in their on-screen order) and, for PDFs, `reviewerFor`
// (the reviewer options per file). A drawing with unsaved markup asks first.
let nav = null; // { list, i, opts }
const viewerOpen = () => !!document.querySelector('.overlay, .dr-viewer');
function navPill() {
  document.querySelector('.fv-nav')?.remove();
  const slot = document.querySelector('.dr-viewer .dr-filenav');
  if (slot) slot.replaceChildren();
  if (!nav || nav.list.length < 2 || !viewerOpen()) return;
  // Over a drawing the switcher is a small ‹ 2 / 5 › pill in the viewer's own
  // top bar, beside the title (markup v2 §1): the bottom centre and the sheet
  // belong to the markup tools.
  if (slot) {
    const f = nav.list[nav.i];
    slot.innerHTML = `<button type="button" data-fv="-1" aria-label="Previous file in the folder (left arrow)" ${nav.i <= 0 ? 'disabled' : ''}>‹</button>
      <span style="padding:0 4px;letter-spacing:0.06em;" title="${escapeHtml(String(f?.name || ''))}">${nav.i + 1} / ${nav.list.length}</span>
      <button type="button" data-fv="1" aria-label="Next file in the folder (right arrow)" ${nav.i >= nav.list.length - 1 ? 'disabled' : ''}>›</button>`;
    slot.querySelectorAll('[data-fv]').forEach((b) => b.addEventListener('click', () => step(Number(b.dataset.fv))));
    return;
  }
  const el = document.createElement('div');
  el.className = 'fv-nav';
  el.setAttribute('role', 'group');
  el.setAttribute('aria-label', 'Move between files');
  const f = nav.list[nav.i];
  el.innerHTML = `<button type="button" data-fv="-1" aria-label="Previous file (left arrow)" ${nav.i <= 0 ? 'disabled' : ''}>‹</button>
    <span><b>${nav.i + 1} of ${nav.list.length}</b> · ${escapeHtml(String(f?.name || '').slice(0, 48))}</span>
    <button type="button" data-fv="1" aria-label="Next file (right arrow)" ${nav.i >= nav.list.length - 1 ? 'disabled' : ''}>›</button>`;
  document.body.appendChild(el);
  el.querySelectorAll('[data-fv]').forEach((b) => b.addEventListener('click', () => step(Number(b.dataset.fv))));
}
async function step(dir) {
  if (!nav) return;
  const j = nav.i + dir;
  if (j < 0 || j >= nav.list.length) return;
  // Unsaved markup on a drawing is never thrown away silently.
  const dr = document.querySelector('.dr-viewer');
  if (dr?.__api?.hasUnsavedMarkup?.() && !window.confirm('This sheet has markup you haven’t saved. Leave it and open the next file?')) return;
  if (dr) { const m = await import('./drawing-review.js'); m.closeDrawingReview(); }
  document.querySelector('.overlay .ov-close')?.click();
  nav.i = j;
  const f = nav.list[j];
  const o = nav.opts;
  openFile(f, { ...o, reviewer: o.reviewerFor ? o.reviewerFor(f, j) : o.reviewer, __nav: true });
}
let navWired = false;
function wireNav() {
  if (navWired) return;
  navWired = true;
  document.addEventListener('keydown', (e) => {
    if (!nav || !viewerOpen() || e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    const t = e.target;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
    // The 3D and CAD viewers keep their own arrows when they use them.
    if (t?.closest?.('canvas, .cad-stage')) return;
    e.preventDefault();
    step(e.key === 'ArrowRight' ? 1 : -1);
  }, true);
  // The pill goes when the viewer goes.
  new MutationObserver(() => {
    if (!viewerOpen()) document.querySelector('.fv-nav')?.remove();
    else if (nav && !document.querySelector('.fv-nav') && !document.querySelector('.dr-viewer .dr-filenav button')) navPill(); // a viewer that mounts late (the reviewer loads first)
  }).observe(document.body, { childList: true });
}

export function openFile(f, opts = {}) {
  if (opts.siblings) {
    const list = opts.siblings.filter((x) => x && !isFolderMime(x.mimeType));
    const i = list.findIndex((x) => x.id === f.id);
    nav = i >= 0 ? { list, i, opts: { siblings: undefined, reviewerFor: opts.reviewerFor, reviewer: opts.reviewer } } : null;
  } else if (!opts.__nav) nav = null;
  wireNav();
  openFileOnly(f, opts);
  setTimeout(navPill, 0);
}

function openFileOnly(f, opts = {}) {
  if (isCad(f)) { openCadViewer(f); return; }
  if (isTedds(f)) { openTeddsViewer(f); return; }
  if (isMasonry(f)) { openMasonryViewer(f); return; }
  // Every PDF opens in the reviewer — the classic viewer is retired (Karan 10/08).
  if (isPdf(f)) { openInReviewer(f, opts.reviewer || {}); return; }
  if (is3d(f)) { openThreeDViewer(f); return; }
  if (isPointCloud(f)) { openPointCloudViewer(f); return; }
  const url = previewUrl(f);
  if (!url) { window.open(safeUrl(f.webViewLink), '_blank', 'noopener'); return; }
  const { content } = openOverlay({
    title: f.name,
    subtitle: fmtDay(f.modifiedTime ? new Date(f.modifiedTime) : null),
    wide: true,
    actionsHtml: `<a class="ov-link" href="${escapeHtml(safeUrl(f.webViewLink || url))}" target="_blank" rel="noopener">Open in Drive →</a>`,
  });
  content.classList.add('viewer');
  content.innerHTML = `<iframe class="viewer-iframe" src="${escapeHtml(url)}" allow="autoplay"></iframe>`;
}

// Warm the PDF engine + reviewer chunk once a documents surface is on screen
// (perf 08/08): both are cached (vendor 7d, /js immutable) so this is usually
// just parse — done at idle, the first click on a sheet skips the wait.
let viewersWarm = false;
function warmViewers() {
  if (viewersWarm) return; viewersWarm = true;
  const go = () => {
    import('/vendor/pdfjs/build/pdf.mjs').catch(() => {});
    import('./drawing-review.js').catch(() => {});
  };
  if ('requestIdleCallback' in window) requestIdleCallback(go, { timeout: 4000 }); else setTimeout(go, 1500);
}

/** Drive folder panel: browse the project folder tree as the signed-in user. */
export function drivePanel(container, { folderId, folderUrl = '', reviewer = null } = {}) {
  warmViewers();
  const body = document.createElement('div');
  body.className = 'g-body';
  container.appendChild(body);
  if (!folderId) {
    body.innerHTML = `<p class="g-note">No Drive folder linked to this project in Track.</p>`;
    return;
  }
  const rootUrl = folderUrl || `https://drive.google.com/drive/folders/${encodeURIComponent(folderId)}`;
  let path = [{ id: folderId, name: 'Project folder', url: rootUrl }];

  async function render() {
    body.innerHTML = `<p class="g-note">Loading…</p>`;
    const here = path[path.length - 1];
    let files;
    try { files = await listFolder(here.id); }
    catch (e) { body.innerHTML = `<p class="g-note error">${escapeHtml(e.message || e)}</p>`; return; }

    const crumbs = path.map((p, i) => i === path.length - 1
      ? `<span class="g-crumb here">${escapeHtml(p.name)}</span>`
      : `<button class="g-crumb" data-i="${i}">${escapeHtml(p.name)}</button>`
    ).join('<span class="g-crumb-sep">/</span>');

    const rows = files.length ? files.map((f, i) => `
      <div class="g-file-row" data-i="${i}" role="button" tabindex="0">
        <span class="g-file-tag${isFolderMime(f.mimeType) ? ' dir' : ''}">${escapeHtml(fileTag(f))}</span>
        <span class="g-file-name">${escapeHtml(f.name)}</span>
        <span class="g-file-meta">${escapeHtml(fmtDay(f.modifiedTime ? new Date(f.modifiedTime) : null))}</span>
      </div>`).join('')
      : `<p class="g-note">Folder is empty (or not shared with you).</p>`;

    const hereUrl = here.url || `https://drive.google.com/drive/folders/${encodeURIComponent(here.id)}`;
    // Files scroll INSIDE their own container (Karan 13/07) — a big folder used
    // to cascade down the whole page. Crumbs + "open in Drive" stay put.
    body.innerHTML = `
      ${path.length > 1 ? `<div class="g-crumbs">${crumbs}</div>` : ''}
      <div class="g-file-scroll">${rows}</div>
      <a class="g-open-folder" href="${escapeHtml(safeUrl(hereUrl))}" target="_blank" rel="noopener">Open this folder in Drive →</a>`;

    body.querySelectorAll('.g-crumb[data-i]').forEach((c) => c.addEventListener('click', () => {
      path = path.slice(0, +c.dataset.i + 1);
      render();
    }));
    body.querySelectorAll('.g-file-row').forEach((row) => {
      const f = files[+row.dataset.i];
      const open = () => {
        if (isFolderMime(f.mimeType)) { path.push({ id: f.id, name: f.name, url: f.webViewLink }); render(); }
        else openFile(f, { ...(reviewer ? { reviewer, reviewerFor: (g) => ({ ...reviewer, reference: g.name, title: g.name }) } : {}), siblings: files });
      };
      row.addEventListener('click', open);
      row.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') open(); });
    });
  }

  wire(body, render);
}
